import { useAuth as useClerkAuth, useUser, useOrganization } from '@clerk/clerk-react';
import { useCallback, useMemo, useEffect } from 'react';
import { api } from '../lib/api';

interface User {
  id: string;
  email: string;
  name?: string;
  role: string;
  settings: Record<string, unknown>;
  imageUrl?: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  settings: Record<string, unknown>;
  imageUrl?: string;
}

export function useAuth() {
  const { isLoaded, isSignedIn, signOut, getToken } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const { organization: clerkOrg } = useOrganization();

  // Wire the API client to ask Clerk for a fresh token on every request.
  // Clerk's getToken() is internally cached + auto-refreshes near expiry,
  // so this is cheap. Without it, the cached JWT in localStorage goes stale
  // after ~60s and every API call 401s.
  useEffect(() => {
    api.setTokenGetter(isSignedIn ? () => getToken() : null);
    return () => api.setTokenGetter(null);
  }, [isSignedIn, getToken]);

  // Sync user to D1 database when authenticated
  useEffect(() => {
    if (!isSignedIn || !clerkUser) return;

    // Check if already synced this session
    const syncKey = `synced:${clerkUser.id}`;
    if (sessionStorage.getItem(syncKey)) return;

    const syncUser = async () => {
      try {
        console.log('[Auth] Syncing user to backend...');
        const token = await getToken();
        if (token) {
          api.setToken(token);
          await api.syncUser({
            name: clerkUser.fullName || clerkUser.firstName || undefined,
            orgName: clerkOrg?.name,
          });
          sessionStorage.setItem(syncKey, 'true');
          console.log('[Auth] User synced successfully');
        }
      } catch (error) {
        console.error('[Auth] Failed to sync user:', error);
      }
    };

    syncUser();
  }, [isSignedIn, clerkUser, clerkOrg, getToken]);

  // Map Clerk user to our User type
  const user: User | null = useMemo(() => {
    if (!clerkUser) return null;
    return {
      id: clerkUser.id,
      email: clerkUser.primaryEmailAddress?.emailAddress || '',
      name: clerkUser.fullName || clerkUser.firstName || undefined,
      role: clerkOrg ? (clerkUser.organizationMemberships?.[0]?.role || 'member') : 'owner',
      settings: (clerkUser.publicMetadata as Record<string, unknown>) || {},
      imageUrl: clerkUser.imageUrl,
    };
  }, [clerkUser, clerkOrg]);

  // Map Clerk organization to our Organization type
  const organization: Organization | null = useMemo(() => {
    if (!clerkOrg) {
      // If no org, create a personal workspace
      if (clerkUser) {
        return {
          id: `personal-${clerkUser.id}`,
          name: `${clerkUser.firstName || 'Personal'}'s Workspace`,
          slug: clerkUser.id,
          settings: {},
        };
      }
      return null;
    }
    return {
      id: clerkOrg.id,
      name: clerkOrg.name,
      slug: clerkOrg.slug || clerkOrg.id,
      settings: (clerkOrg.publicMetadata as Record<string, unknown>) || {},
      imageUrl: clerkOrg.imageUrl,
    };
  }, [clerkOrg, clerkUser]);

  // Get auth token for API requests
  const getAuthToken = useCallback(async () => {
    return getToken();
  }, [getToken]);

  // Update user settings via our API
  const updateUserSettings = useCallback(async (settings: Record<string, unknown>) => {
    const token = await getToken();
    if (token) {
      api.setToken(token);
    }
    return api.updateUserSettings(settings);
  }, [getToken]);

  // Logout
  const logout = useCallback(async () => {
    api.setToken(null);
    await signOut();
  }, [signOut]);

  // Refresh auth data from Clerk (reloads user metadata)
  const refreshAuth = useCallback(async () => {
    if (clerkUser) {
      await clerkUser.reload();
    }
  }, [clerkUser]);

  return {
    user,
    organization,
    isAuthenticated: isSignedIn ?? false,
    isLoading: !isLoaded,
    error: null,
    getAuthToken,
    updateUserSettings,
    logout,
    refreshAuth,
    // Keep for compatibility
    login: async () => { throw new Error('Use Clerk SignIn component'); },
    register: async () => { throw new Error('Use Clerk SignUp component'); },
    checkAuth: async () => {},
  };
}
